import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageShell } from '@/components/site/PageShell';
import { DocBody } from '@/components/site/DocBody';
import { Seo } from '@/components/seo/Seo';
import { Button } from '@/components/ui/button';
import { getGuide, guides } from '@/content/guides';
import { SITE } from '@/content/site';
import { ArrowLeft } from '@phosphor-icons/react';
import NotFoundPage from './NotFoundPage';
import { GuideCard } from './GuidesPage';

const articleLd = (g) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: g.title,
  description: g.description,
  image: g.image,
  datePublished: g.published,
  author: { '@type': 'Organization', name: SITE.name },
  publisher: { '@type': 'Organization', name: SITE.name },
});

export default function GuideArticlePage() {
  const { slug } = useParams();
  const g = getGuide(slug);
  const ld = useMemo(() => (g ? articleLd(g) : null), [g]);
  if (!g) return <NotFoundPage />;
  const more = guides.filter((x) => x.slug !== slug).slice(0, 2);

  return (
    <PageShell testId="guide-article-page">
      <Seo title={g.title} description={g.description} type="article" image={g.image} jsonLd={ld} />
      <article className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <Link to="/guides" className="inline-flex items-center gap-1 text-sm text-[#71717A] hover:text-[#0047FF]" data-testid="guide-back-link">
          <ArrowLeft size={14} /> All guides
        </Link>
        <p className="mt-8 font-mono text-xs uppercase tracking-widest text-[#0047FF]">{g.category} · {g.readMinutes} min read</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#09090B]" data-testid="page-title">{g.title}</h1>
        <p className="mt-5 text-base md:text-lg text-[#71717A] leading-relaxed">{g.description}</p>
        <img src={g.image} alt={g.imageAlt} className="mt-10 w-full aspect-[16/9] object-cover border border-[#E4E4E7]" />
        <div className="mt-12">
          {g.sections.map((s) => (
            <section key={s.heading} className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-[#09090B] mb-4">{s.heading}</h2>
              <DocBody body={s.body} />
            </section>
          ))}
        </div>
        <div className="mt-6 p-8 bg-[#09090B] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <p className="font-heading text-lg font-bold">Ready to put it into practice?</p>
          <Button asChild className="bg-[#0047FF] hover:bg-[#0037CC] text-white" data-testid="guide-play-now">
            <Link to="/auth">Play on DashLink</Link>
          </Button>
        </div>
      </article>
      <section className="border-t border-[#E4E4E7] bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="font-heading text-xl font-bold tracking-tight text-[#09090B] mb-8">More guides</h2>
          <div className="grid gap-8 md:grid-cols-2">{more.map((x) => <GuideCard key={x.slug} g={x} />)}</div>
        </div>
      </section>
    </PageShell>
  );
}
